import { useEffect, useState } from 'react'
import { ColorInput, Stack, Switch, TextInput, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { ActionButton, AppModal } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import { notifyProjectsChanged } from '@/lib/projects-events'
import type { ProjectRow } from '@/types/api'

type Props = {
  opened: boolean
  onClose: () => void
  project: ProjectRow | null
}

export function ProjectQuickEditModal({ opened, onClose, project }: Props) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#3B82F6')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (!opened || !project) return
    let cancelled = false
    setLoading(true)
    void api
      .project(project.id)
      .then((d) => {
        if (cancelled) return
        setName(d.name || '')
        setDescription(d.description ?? '')
        setColor(d.color || '#3B82F6')
        setIsActive(d.is_active !== false)
      })
      .catch(() => {
        if (!cancelled) {
          setName(project.name)
          setDescription(project.description ?? '')
          setColor(project.color || '#3B82F6')
          setIsActive(project.is_active !== false)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [opened, project])

  const save = async () => {
    if (!project) return
    const n = name.trim()
    if (!n) {
      showNotification({ title: 'Форма', message: 'Укажите название', color: 'yellow' })
      return
    }
    setSaving(true)
    try {
      await api.projectUpdate(project.id, {
        name: n,
        description: description.trim() || null,
        color,
        is_active: isActive,
      })
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
      notifyProjectsChanged()
      onClose()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="Изменить проект"
      size="md"
      footer={
        <>
          <ActionButton action="cancel" onClick={onClose} />
          <ActionButton action="save" loading={saving} onClick={() => void save()} />
        </>
      }
    >
      <Stack gap="sm">
        <TextInput label="Название" required value={name} onChange={(e) => setName(e.target.value)} disabled={loading} />
        <Textarea label="Описание" minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} disabled={loading} />
        <ColorInput label="Цвет" value={color} onChange={setColor} disabled={loading} format="hex" />
        <Switch label="Активен" checked={isActive} onChange={(e) => setIsActive(e.currentTarget.checked)} disabled={loading} />
      </Stack>
    </AppModal>
  )
}
